import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { api, type AlertType, type ReasonCode, type DismissType } from '../../lib/api';
import { unwrapApi } from '../../lib/unwrapApi';

interface DismissAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  alertType: AlertType;
  entityId: string;
  clientName: string;
  issueDescription: string;
  onDismissed?: () => void;
}

const REASON_OPTIONS: { value: ReasonCode; label: string; description: string }[] = [
  { value: 'verified_correct', label: 'Verified correct', description: 'I verified this data is accurate' },
  { value: 'intentionally_blank', label: 'Intentionally blank', description: 'This field is meant to be empty' },
  { value: 'client_confirmed', label: 'Client confirmed', description: 'The client confirmed this is correct' },
  { value: 'not_applicable', label: 'Not applicable', description: 'This alert doesn\'t apply to this client' },
  { value: 'other', label: 'Other', description: 'Specify reason in notes' },
];

const DISMISS_TYPE_OPTIONS: { value: DismissType; label: string; description: string }[] = [
  {
    value: 'one_time',
    label: 'One-time exception',
    description: 'Dismiss this specific alert. It will reappear if the data changes.'
  },
  {
    value: 'ongoing',
    label: 'Ongoing exception',
    description: 'Permanently dismiss this alert for this client, even if data changes.'
  },
];

export function DismissAlertModal({
  isOpen,
  onClose,
  alertType,
  entityId,
  clientName,
  issueDescription,
  onDismissed,
}: DismissAlertModalProps) {
  const queryClient = useQueryClient();
  const modalRef = useRef<HTMLDivElement>(null);
  const [reasonCode, setReasonCode] = useState<ReasonCode | ''>('');
  const [reasonNote, setReasonNote] = useState('');
  const [dismissType, setDismissType] = useState<DismissType>('one_time');

  // Fetch alert type configuration
  const { data: alertTypeConfig } = useQuery({
    queryKey: ['alert-types'],
    queryFn: async () => {
      const response = await api.alerts.getAlertTypes();
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Derive config for current alert type
  const currentConfig = alertTypeConfig?.[alertType];
  const allowOngoing = currentConfig?.allowOngoing ?? true; // Default to true for backwards compat
  const expireDays = currentConfig?.expireDays;

  // Dynamic modal title based on alert type
  const modalTitle = allowOngoing ? 'Log Exception' : 'Dismiss Alert';

  // Reset to one_time if ongoing is not allowed for this alert type
  useEffect(() => {
    if (!allowOngoing && dismissType === 'ongoing') {
      setDismissType('one_time');
    }
  }, [allowOngoing, dismissType]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Focus modal on open
  useEffect(() => {
    if (isOpen) {
      modalRef.current?.focus();
    }
  }, [isOpen]);

  const dismissMutation = useMutation({
    mutationFn: () =>
      api.alerts.dismiss({
        alertType,
        entityType: 'client',
        entityId,
        reasonCode: reasonCode as ReasonCode,
        reasonNote: reasonNote.trim() || undefined,
        dismissType,
      }).then(unwrapApi),
    onSuccess: () => {
      // Invalidate dashboard queries to refresh counts
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onDismissed?.();
      handleClose();
    },
  });

  const handleClose = () => {
    setReasonCode('');
    setReasonNote('');
    setDismissType('one_time');
    onClose();
  };

  const canSubmit =
    reasonCode !== '' &&
    (reasonCode !== 'other' || reasonNote.trim().length > 0);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dismiss-modal-title"
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-white w-full sm:w-[520px] sm:rounded-2xl sm:max-h-[90vh] max-h-[95vh] overflow-hidden flex flex-col animate-slide-up outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-forest-800 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 id="dismiss-modal-title" className="font-display text-lg font-semibold">{modalTitle}</h2>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Client Info */}
          <div className="bg-sage-50 rounded-xl p-4">
            <p className="text-sm text-sage-600 mb-1">Client</p>
            <p className="font-medium text-forest-900">{clientName}</p>
            <p className="text-sm text-sage-600 mt-2 mb-1">Issue</p>
            <p className="text-sm text-forest-700">{issueDescription}</p>
          </div>

          {/* Exception Type Selection */}
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-2">
              Exception type
            </label>
            {allowOngoing ? (
              // Show both options for data quality alerts
              <div className="grid grid-cols-2 gap-3">
                {DISMISS_TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setDismissType(option.value)}
                    className={`p-3 rounded-xl border-2 text-left transition-colors ${
                      dismissType === option.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-sage-200 hover:border-sage-300'
                    }`}
                  >
                    <p className="font-medium text-forest-900 text-sm">{option.label}</p>
                    <p className="text-xs text-sage-500 mt-1">{option.description}</p>
                  </button>
                ))}
              </div>
            ) : (
              // Show info message about expiration for rolling/point-in-time alerts
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800">
                  This dismissal will expire in {expireDays} day{expireDays !== 1 ? 's' : ''}.
                </p>
              </div>
            )}
          </div>

          {/* Reason Selection */}
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {REASON_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`
                    flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors
                    ${reasonCode === option.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-sage-200 hover:border-sage-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="reasonCode"
                    value={option.value}
                    checked={reasonCode === option.value}
                    onChange={(e) => setReasonCode(e.target.value as ReasonCode)}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="font-medium text-forest-900">{option.label}</p>
                    <p className="text-sm text-sage-600">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Note Field */}
          <div>
            <label className="block text-sm font-medium text-forest-700 mb-2">
              Note {reasonCode === 'other' && <span className="text-red-500">*</span>}
              {reasonCode !== 'other' && <span className="text-sage-500 font-normal">(optional)</span>}
            </label>
            <textarea
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
              placeholder={reasonCode === 'other' ? 'Please explain the reason...' : 'Add any additional context...'}
              rows={3}
              className={`
                w-full px-3 py-2.5 rounded-xl border-2 focus:outline-none resize-none
                ${reasonCode === 'other' && !reasonNote.trim()
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-sage-200 focus:border-primary-500'
                }
              `}
            />
            {reasonCode === 'other' && !reasonNote.trim() && (
              <p className="text-sm text-red-600 mt-1">Note is required when selecting "Other"</p>
            )}
          </div>

          {/* Error Display */}
          {dismissMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-sm text-red-700">
                Failed to log exception. Please try again.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-sage-200 px-6 py-4 flex gap-3">
          <Button variant="ghost" onClick={handleClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => dismissMutation.mutate()}
            disabled={!canSubmit || dismissMutation.isPending}
            className="flex-1"
          >
            {dismissMutation.isPending ? 'Saving...' : 'Log Exception'}
          </Button>
        </div>
      </div>
    </div>
  );
}
